import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { 
  languageNames, 
  languageFlags, 
  primaryLanguages, 
  languagesByRegion,
  Language 
} from '@/context/languages/languageData';

export const LanguageSelectorFooter: React.FC = () => {
  const { language, changeLanguageAndNavigate } = useLanguage();
  const [showAll, setShowAll] = useState(false);

  const handleLanguageChange = (newLang: Language) => {
    changeLanguageAndNavigate(newLang);
  };

  return (
    <div className="border-t border-border/40 pt-8 mt-8">
      <div className="flex items-center gap-2 mb-4">
        <Globe className="h-5 w-5 text-muted-foreground" />
        <h3 className="font-semibold text-lg">Choose Your Language</h3>
      </div>

      {/* Primary Languages - Always Visible */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
        {primaryLanguages.map((lang) => (
          <Button
            key={lang}
            variant={language === lang ? "default" : "outline"}
            size="sm"
            onClick={() => handleLanguageChange(lang)}
            className="justify-start gap-2"
          >
            <span className="text-base">{languageFlags[lang]}</span>
            <span className="text-sm">{languageNames[lang]}</span>
          </Button>
        ))}
      </div>

      {/* Toggle Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowAll(!showAll)}
        className="w-full justify-center gap-2 text-muted-foreground hover:text-foreground"
      >
        <span>{showAll ? 'Show Less' : 'View All 40 Languages'}</span>
        {showAll ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </Button>

      {/* All Languages - Expandable */}
      {showAll && (
        <div className="mt-4 space-y-6">
          {/* Western Europe */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Western Europe</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {languagesByRegion.western.filter(lang => !primaryLanguages.includes(lang)).map((lang) => (
                <Button
                  key={lang}
                  variant={language === lang ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleLanguageChange(lang)}
                  className="justify-start gap-2"
                >
                  <span className="text-base">{languageFlags[lang]}</span>
                  <span className="text-sm">{languageNames[lang]}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Nordic Countries */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Nordic Countries</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {languagesByRegion.nordic.map((lang) => (
                <Button
                  key={lang}
                  variant={language === lang ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleLanguageChange(lang)}
                  className="justify-start gap-2"
                >
                  <span className="text-base">{languageFlags[lang]}</span>
                  <span className="text-sm">{languageNames[lang]}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Central Europe */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Central Europe</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {languagesByRegion.central.map((lang) => (
                <Button
                  key={lang}
                  variant={language === lang ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleLanguageChange(lang)}
                  className="justify-start gap-2"
                >
                  <span className="text-base">{languageFlags[lang]}</span>
                  <span className="text-sm">{languageNames[lang]}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Baltic & Southern Europe */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Baltic & Southern Europe</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {[...languagesByRegion.baltic, ...languagesByRegion.southern].map((lang) => (
                <Button
                  key={lang}
                  variant={language === lang ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleLanguageChange(lang)}
                  className="justify-start gap-2"
                >
                  <span className="text-base">{languageFlags[lang]}</span>
                  <span className="text-sm">{languageNames[lang]}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Asia */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Asia</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
              {languagesByRegion.asian.map((lang) => (
                <Button
                  key={lang}
                  variant={language === lang ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleLanguageChange(lang)}
                  className="justify-start gap-2"
                >
                  <span className="text-base">{languageFlags[lang]}</span>
                  <span className="text-sm">{languageNames[lang]}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Middle East & Eastern Europe */}
          <div>
            <h4 className="text-sm font-medium text-muted-foreground mb-2">Middle East & Eastern Europe</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-2">
              {[...languagesByRegion.middle_east, ...languagesByRegion.eastern].map((lang) => (
                <Button
                  key={lang}
                  variant={language === lang ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleLanguageChange(lang)}
                  className="justify-start gap-2"
                >
                  <span className="text-base">{languageFlags[lang]}</span>
                  <span className="text-sm">{languageNames[lang]}</span>
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-muted-foreground mt-4 text-center">
        All pages available in 40 languages • Professional mental health support worldwide
      </p>
    </div>
  );
};

export default LanguageSelectorFooter;
